'use client'

import { useEffect, useRef, type ReactNode } from 'react'
import { X } from 'lucide-react'

interface FilterDrawerProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: ReactNode
}

export function FilterDrawer({ isOpen, onClose, title = 'Filters', children }: FilterDrawerProps) {
  const backdropRef = useRef<HTMLDivElement>(null)
  const font = { fontFamily: 'var(--font-inter, Inter, sans-serif)' }

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  if (!isOpen) return null

  return (
    <div
      ref={backdropRef}
      className="fixed inset-0 z-50 flex items-end sm:hidden"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={(e) => {
        if (e.target === backdropRef.current) onClose()
      }}
    >
      <div className="absolute inset-0 bg-black/40" />
      <div className="relative w-full rounded-t-2xl border-t border-[var(--color-border-default)] bg-[var(--color-bg-surface)] px-4 pb-8 pt-4 shadow-2xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-base font-semibold text-[var(--color-text-primary)]" style={font}>{title}</h3>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full text-[var(--color-text-muted)] hover:bg-[var(--color-bg-elevated)]"
            aria-label="Close filters"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="max-h-[60vh] space-y-4 overflow-y-auto">
          {children}
        </div>
      </div>
    </div>
  )
}
